import { useRef } from 'react';
import { useMutation } from 'react-query';

import { Config, Institute } from '@nw/shared/config';
import {
    getAPIErrorMessage,
    getSessionStorageToken,
    setSessionStorageToken
} from '@nw/shared/utils';

export type InstituteApiVersion = 'v2' | 'v3';

export interface InstitutesResponse {
    institutes: Institute[];
    total_count?: number;
}

const getInstituteUrl = (instituteApiVersion: InstituteApiVersion): string => {
    const baseUrl = `${Config['COMMON_DATA_INSTITUTE_BASE_URL']}`;
    if (instituteApiVersion === 'v3') {
        return baseUrl.replace('/v2/', '/v3/');
    }
    return baseUrl;
};

const isInstitutesResponse = (data: unknown): data is InstitutesResponse =>
    Boolean(data) && Array.isArray((data as InstitutesResponse).institutes);

const readCachedInstitutes = (
    sessionStorageKey: string
): InstitutesResponse | null => {
    try {
        const storedResponse = getSessionStorageToken(sessionStorageKey);
        if (!storedResponse) return null;
        const parsedResponse = JSON.parse(storedResponse);
        return isInstitutesResponse(parsedResponse) ? parsedResponse : null;
    } catch {
        return null;
    }
};

const cacheInstitutes = (
    sessionStorageKey: string,
    data: InstitutesResponse
) => {
    try {
        setSessionStorageToken(sessionStorageKey, data);
    } catch {
        // Ignore storage failures - the response is still usable for this render
    }
};

const fetchInstituteData = async (
    signal: AbortSignal,
    searchValue: string,
    instituteApiVersion: InstituteApiVersion
) => {
    const response = await fetch(getInstituteUrl(instituteApiVersion), {
        signal,
        method: 'POST',
        body: JSON.stringify({
            data: JSON.stringify(
                JSON.stringify({
                    search_value: searchValue,
                    offset: 0,
                    limit: 100
                })
            ),
            clientKeyDetailsId: 1
        }),
        headers: { 'content-type': 'application/json' }
    });

    if (!response.ok) {
        throw new Error(
            `Institutes request failed with status ${response.status}`
        );
    }

    return response.json();
};

export const useGetInstitutesMutation = (
    instituteApiVersion: InstituteApiVersion
) => {
    const activeRequests = useRef<AbortController[]>([]); // Ref to store active AbortControllers

    const getInstitutes = async (params: {
        filterText: string;
    }): Promise<InstitutesResponse> => {
        // Cancel all previous requests
        activeRequests.current.forEach((controller) => controller.abort());
        activeRequests.current = [];

        const abortController = new AbortController();
        activeRequests.current.push(abortController);

        const textToSearch = params.filterText.trim();
        const key: string = textToSearch.toLocaleLowerCase();
        const sessionStorageKey = `institutesName-${instituteApiVersion}-${key}`;

        const cachedResponse = readCachedInstitutes(sessionStorageKey);
        if (cachedResponse) return cachedResponse;

        const data = await fetchInstituteData(
            abortController.signal,
            textToSearch,
            instituteApiVersion
        );

        if (!isInstitutesResponse(data)) {
            throw new Error('Institutes response does not contain a list');
        }

        cacheInstitutes(sessionStorageKey, data);

        return data;
    };

    const { mutateAsync, error, status } = useMutation(getInstitutes);

    const getInstitutesApi = (params: { filterText: string }) => {
        return mutateAsync(params);
    };

    return {
        getInstitutesApi,
        error: error ? getAPIErrorMessage(error) : '',
        status
    };
};
